import React from 'react';
import { useIntl } from 'react-intl';

import {
  customSettingsKeys,
  customSettingsOptionValues,
  getTimePeriodFieldOptions,
  valueKeys,
} from '~/common/constants/workflow';
import { formatDate } from '~/eds/src/utils/formatDate';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { WorkflowFieldDefaultValueInputType } from '~/enums/WorkflowFieldDefaultValueInputType';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';

import AvailableOptions from './AvailableOptions';
import CustomSettingsInput from './CustomSettingsInput';
import DefaultValueInput from './DefaultValueInput';
import { Column, Row } from './SettingsLayout';

function FieldSettings({
  customSettings,
  fieldType,
  onUpdate,
  isDefaultValueInvalid,
}) {
  const intl = useIntl();
  const { defaultValue = {}, options = [] } = customSettings;
  const customSettingKey = customSettingsKeys[fieldType];
  const customSettingsOptionValue = customSettingsOptionValues[fieldType];
  const valueKey = valueKeys[fieldType];

  function customSettingsUpdateFactory(key) {
    return (updatedValue) => {
      let updatedSettings = {
        ...customSettings,
        [key]: updatedValue,
      };
      // should reset defaultValue when changing email_type
      if (
        fieldType === WorkflowFieldType.Email &&
        key === customSettingKey.EMAIL_TYPE
      ) {
        const shouldResetValue =
          updatedValue !== customSettingsOptionValue.EMAIL_TYPE.MANUAL;
        if (shouldResetValue) {
          updatedSettings = {
            ...updatedSettings,
            defaultValue: {
              value: null,
            },
          };
        }
      }
      // should reset defaultValue, min and max when changing operator
      if (
        fieldType === WorkflowFieldType.Percentage &&
        key === customSettingKey.OPERATOR
      ) {
        delete updatedSettings.max;
        delete updatedSettings.min;
        updatedSettings = {
          ...updatedSettings,
          defaultValue: {
            value: null,
          },
        };
      }
      // remove decimal if allowDecimals is changed to false.
      if (
        fieldType === WorkflowFieldType.Number &&
        key === customSettingKey.ALLOW_DECIMALS
      ) {
        if (!updatedValue && updatedSettings.defaultValue?.value) {
          updatedSettings = {
            ...updatedSettings,
            defaultValue: {
              value: Math.trunc(updatedSettings.defaultValue.value),
            },
          };
        }
      }

      onUpdate(updatedSettings);
    };
  }

  function defaultValueUpdateFactory(key = 'value') {
    return (updatedValue) => {
      const updatedSettings = {
        ...customSettings,
        defaultValue: {
          ...customSettings.defaultValue,
          [key]: updatedValue,
        },
      };
      onUpdate(updatedSettings);
    };
  }

  function handleUpdateOptions(updatedOptions) {
    const updatedSettings = {
      ...customSettings,
      options: updatedOptions,
    };
    onUpdate(updatedSettings);
  }

  switch (fieldType) {
    case WorkflowFieldType.Address: {
      const documentPlacementOptions = [
        {
          label: intl.formatMessage({
            id: 'FieldSettings.singleLine',
            defaultMessage: 'Single line',
            description: 'Document placement option for single line',
          }),
          value: customSettingsOptionValue.DOCUMENT_PLACEMENT.SINGLE_LINE,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.multipleLines',
            defaultMessage: 'Multiple lines',
            description: 'Document placement option for multiple lines',
          }),
          value: customSettingsOptionValue.DOCUMENT_PLACEMENT.MULTIPLE_LINES,
        },
      ];
      return (
        <Column space={6}>
          <Row>
            <DefaultValueInput
              defaultValue={defaultValue}
              id="address--country"
              inputType={WorkflowFieldDefaultValueInputType.CountrySingleSelect}
              label={intl.formatMessage({
                id: 'FieldSettings.defaultCountry',
                defaultMessage: 'Default Country',
                description: 'Label for default country field',
              })}
              placeholder={intl.formatMessage({
                id: 'FieldSettings.countryPlaceholder',
                defaultMessage: 'Country',
                description: 'Placeholder for country field',
              })}
              valueKey={valueKey.COUNTRY}
              width="input.l.width"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
            <CustomSettingsInput
              customSettings={customSettings}
              id="address--documentPlacement"
              inputType="SINGLE_SELECT"
              label={intl.formatMessage({
                id: 'FieldSettings.documentPlacement',
                defaultMessage: 'Document Placement',
                description: 'Label for document placement setting',
              })}
              options={documentPlacementOptions}
              settingsKey={customSettingKey.DOCUMENT_PLACEMENT}
              width="fullWidth"
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
          </Row>
          <DefaultValueInput
            defaultValue={defaultValue}
            id="address--street1"
            inputType={WorkflowFieldDefaultValueInputType.TextInput}
            label={intl.formatMessage({
              id: 'FieldSettings.defaultAddress',
              defaultMessage: 'Default Address',
              description: 'Label for default address field',
            })}
            placeholder={intl.formatMessage({
              id: 'FieldSettings.addressLine1Placeholder',
              defaultMessage: 'Address line 1',
              description: 'Placeholder for address line 1',
            })}
            valueKey={valueKey.STREET_1}
            width="l"
            defaultValueUpdateFactory={defaultValueUpdateFactory}
          />
          <DefaultValueInput
            defaultValue={defaultValue}
            id="address--street2"
            inputType={WorkflowFieldDefaultValueInputType.TextInput}
            label=""
            placeholder={intl.formatMessage({
              id: 'FieldSettings.addressLine2Placeholder',
              defaultMessage: 'Address line 2',
              description: 'Placeholder for address line 2',
            })}
            valueKey={valueKey.STREET_2}
            width="l"
            defaultValueUpdateFactory={defaultValueUpdateFactory}
          />
          <Row>
            <DefaultValueInput
              defaultValue={defaultValue}
              id="address--city"
              inputType={WorkflowFieldDefaultValueInputType.TextInput}
              label={intl.formatMessage({
                id: 'FieldSettings.defaultCity',
                defaultMessage: 'Default Town / City',
                description: 'Label for default city field',
              })}
              placeholder={intl.formatMessage({
                id: 'FieldSettings.cityPlaceholder',
                defaultMessage: 'City',
                description: 'Placeholder for city field',
              })}
              valueKey={valueKey.CITY}
              width="m"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
            <DefaultValueInput
              defaultValue={defaultValue}
              id="address--state"
              inputType={WorkflowFieldDefaultValueInputType.StateSingleSelect}
              label={intl.formatMessage({
                id: 'FieldSettings.defaultState',
                defaultMessage: 'Default State',
                description: 'Label for default state field',
              })}
              placeholder={intl.formatMessage({
                id: 'FieldSettings.statePlaceholder',
                defaultMessage: 'State',
                description: 'Placeholder for state field',
              })}
              valueKey={valueKey.STATE}
              width="input.m.width"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
          </Row>
          <DefaultValueInput
            defaultValue={defaultValue}
            id="address--zipCode"
            inputType={WorkflowFieldDefaultValueInputType.TextInput}
            label={intl.formatMessage({
              id: 'FieldSettings.defaultZip',
              defaultMessage: 'Default Zip',
              description: 'Label for default zip code field',
            })}
            valueKey={valueKey.ZIP_CODE}
            width="m"
            defaultValueUpdateFactory={defaultValueUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.Checkbox: {
      return (
        <Column>
          <Row>
            <AvailableOptions
              id="checkbox--options"
              options={options}
              values={options.map((option) => option.value)}
              width="l"
              onChange={handleUpdateOptions}
            />
            <DefaultValueInput
              defaultValue={defaultValue}
              disabled={!options.length}
              id="checkbox--VALUE"
              inputType={WorkflowFieldDefaultValueInputType.MultiSelect}
              isRequired={false}
              label={intl.formatMessage({
                id: 'FieldSettings.checkedByDefault',
                defaultMessage: 'Checked by default',
                description: 'Label for default checked options',
              })}
              options={options}
              valueKey={valueKey.VALUE}
              width="100%"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
          </Row>
          <CustomSettingsInput
            customSettings={customSettings}
            description={intl.formatMessage({
              id: 'FieldSettings.enableOtherDescription',
              defaultMessage:
                'User will have to choose from the options listed above',
              description: 'Description for enable other option setting',
            })}
            id="checkbox--ENABLE_OTHER_OPTION"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.enableOtherOption',
              defaultMessage: 'Enable "Other" option',
              description: 'Label for enable other option checkbox',
            })}
            settingsKey={customSettingKey.ENABLE_OTHER_OPTION}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
          <CustomSettingsInput
            customSettings={customSettings}
            description={intl.formatMessage({
              id: 'FieldSettings.excludeOtherDescription',
              defaultMessage:
                'For reports and searching purposes, list the entry only as "Other"',
              description: 'Description for exclude other option setting',
            })}
            disabled={!customSettings[customSettingKey.ENABLE_OTHER_OPTION]}
            id="checkbox--EXCLUDE_OTHER_OPTION_IN_DATA_MAPPING"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.excludeOtherOption',
              defaultMessage:
                'Remove entered "Other" value in the data mapping',
              description: 'Label for exclude other option checkbox',
            })}
            settingsKey={customSettingKey.EXCLUDE_OTHER_OPTION_IN_DATA_MAPPING}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.Date: {
      //l10n-ignore: TECH DEBT - use formatDate to avoid localization issues with ticket injection
      const formatOptions = [
        {
          label: formatDate(new Date(), 'us_date'),
          value: customSettingsOptionValue.FORMAT.Default,
        },
        {
          label: formatDate(new Date(), 'eu_date'),
          value: customSettingsOptionValue.FORMAT.EuShort,
        },
        {
          label: formatDate(new Date(), 'us_date'),
          value: customSettingsOptionValue.FORMAT.UsShort,
        },
        {
          label: formatDate(new Date(), 'iso'),
          value: customSettingsOptionValue.FORMAT.Iso,
        },
        {
          label: formatDate(new Date(), 'fixed_long'),
          value: customSettingsOptionValue.FORMAT.Long,
        },
        {
          label: formatDate(new Date(), 'prose'),
          value: customSettingsOptionValue.FORMAT.Prose,
        },
      ];
      return (
        <CustomSettingsInput
          customSettings={customSettings}
          id="date--FORMAT"
          inputType="SINGLE_SELECT"
          label={intl.formatMessage({
            id: 'FieldSettings.dateFormat',
            defaultMessage: 'Date Format',
            description: 'Label for date format selection',
          })}
          options={formatOptions}
          settingsKey={customSettingKey.FORMAT}
          width="l"
          customSettingsUpdateFactory={customSettingsUpdateFactory}
        />
      );
    }
    case WorkflowFieldType.Department: {
      const ruleOptions = [
        {
          label: intl.formatMessage({
            id: 'FieldSettings.allDepartments',
            defaultMessage: 'All departments',
            description: 'Option for all departments rule',
          }),
          value: customSettingsOptionValue.RULE.ALL,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.allDepartmentsExcept',
            defaultMessage: 'All departments, except',
            description: 'Option for all departments except rule',
          }),
          value: customSettingsOptionValue.RULE.ALL_EXCEPT,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.specificDepartments',
            defaultMessage: 'Specific departments',
            description: 'Option for specific departments rule',
          }),
          value: customSettingsOptionValue.RULE.SPECIFIC,
        },
      ];
      return (
        <Column>
          <CustomSettingsInput
            customSettings={customSettings}
            id="department--RULE"
            inputType="SINGLE_SELECT"
            label={intl.formatMessage({
              id: 'FieldSettings.usersWillChooseFrom',
              defaultMessage: 'Users will be able to choose from',
              description: 'Label for department rule selection',
            })}
            options={ruleOptions}
            settingsKey={customSettingKey.RULE}
            width="l"
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
          {[
            customSettingsOptionValue.RULE.ALL_EXCEPT,
            customSettingsOptionValue.RULE.SPECIFIC,
          ].includes(customSettings[customSettingKey.RULE]) && (
            <CustomSettingsInput
              customSettings={customSettings}
              id="department--DEPARTMENTS"
              inputType="DEPARTMENT_MULTI_SELECT"
              placeholder={intl.formatMessage({
                id: 'FieldSettings.selectPlaceholder',
                defaultMessage: 'Select',
                description: 'Placeholder for select inputs',
              })}
              settingsKey={customSettingKey.DEPARTMENTS}
              width="l"
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
          )}
          <CustomSettingsInput
            customSettings={customSettings}
            id="department--ALLOW_MULTIPLE"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.allowMultipleDepartments',
              defaultMessage: 'Allow users to select multiple departments',
              description: 'Label for allow multiple departments checkbox',
            })}
            settingsKey={customSettingKey.ALLOW_MULTIPLE}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.Email: {
      const emailTypeOptions = [
        {
          label: intl.formatMessage({
            id: 'FieldSettings.emailTypeNone',
            defaultMessage: 'None',
            description: 'Email type option for none',
          }),
          value: customSettingsOptionValue.EMAIL_TYPE.NONE,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.emailTypeManual',
            defaultMessage: 'Manually enter a default email',
            description: 'Email type option for manual entry',
          }),
          value: customSettingsOptionValue.EMAIL_TYPE.MANUAL,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.emailTypeSubmitter',
            defaultMessage: "Submitter's email",
            description: 'Email type option for submitter email',
          }),
          value: customSettingsOptionValue.EMAIL_TYPE.SUBMITTER,
        },
      ];
      return (
        <Row>
          <CustomSettingsInput
            customSettings={customSettings}
            id="email--EMAIL_TYPE"
            inputType="SINGLE_SELECT"
            isRequired={false}
            label={intl.formatMessage({
              id: 'FieldSettings.defaultValue',
              defaultMessage: 'Default Value',
              description: 'Label for default value setting',
            })}
            options={emailTypeOptions}
            settingsKey={customSettingKey.EMAIL_TYPE}
            width="l"
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
          {customSettingsOptionValue.EMAIL_TYPE.MANUAL ===
            customSettings[customSettingKey.EMAIL_TYPE] && (
            <DefaultValueInput
              defaultValue={defaultValue}
              id="email--VALUE"
              inputType={WorkflowFieldDefaultValueInputType.Email}
              label={null}
              placeholder={intl.formatMessage({
                id: 'FieldSettings.enterEmail',
                defaultMessage: 'Enter email',
                description: 'Placeholder for email input',
              })}
              valueKey={valueKey.VALUE}
              width="fullWidth"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
          )}
        </Row>
      );
    }
    case WorkflowFieldType.File: {
      const fileLimitOptions = [
        {
          label: intl.formatMessage({
            id: 'FieldSettings.oneAttachment',
            defaultMessage: 'One attachment',
            description: 'File limit option for single attachment',
          }),
          value: customSettingsOptionValue.FILE_LIMIT.SINGLE,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.multipleAttachments',
            defaultMessage: 'Multiple attachments',
            description: 'File limit option for multiple attachments',
          }),
          value: customSettingsOptionValue.FILE_LIMIT.MULTIPLE,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.unlimitedAttachments',
            defaultMessage: 'Unlimited attachments',
            description: 'File limit option for unlimited attachments',
          }),
          value: customSettingsOptionValue.FILE_LIMIT.UNLIMITED,
        },
      ];
      const fileTypeRuleOptions = [
        {
          label: intl.formatMessage({
            id: 'FieldSettings.anyFileType',
            defaultMessage: 'Any',
            description: 'File type rule option for any file type',
          }),
          value: customSettingsOptionValue.FILE_TYPE_RULE.ANY,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.specificFileType',
            defaultMessage: 'Specific file type',
            description: 'File type rule option for specific file types',
          }),
          value: customSettingsOptionValue.FILE_TYPE_RULE.SPECIFIC,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.anyFileTypeExcept',
            defaultMessage: 'Any, except',
            description: 'File type rule option for any except specific',
          }),
          value: customSettingsOptionValue.FILE_TYPE_RULE.ANY_EXCEPT,
        },
      ];
      const fileTypeOptions = Object.values(FileExtensionType).map(
        (fileExtension) => ({
          label: fileExtension.substring(1).toUpperCase(),
          value: fileExtension,
        }),
      );

      return (
        <Column space={6}>
          <Row>
            <CustomSettingsInput
              customSettings={customSettings}
              id="file--FILE_LIMIT"
              inputType="SINGLE_SELECT"
              label={intl.formatMessage({
                id: 'FieldSettings.allowUsersToAttach',
                defaultMessage: 'Allow users to attach',
                description: 'Label for file attachment limit setting',
              })}
              options={fileLimitOptions}
              settingsKey={customSettingKey.FILE_LIMIT}
              width="l"
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
            {customSettings[customSettingKey.FILE_LIMIT] ===
              customSettingsOptionValue.FILE_LIMIT.MULTIPLE && (
              <CustomSettingsInput
                customSettings={customSettings}
                id="file--MIN_FILE_COUNT"
                inputType="NUMBER_INPUT"
                label={intl.formatMessage({
                  id: 'FieldSettings.userMustAttachAtLeast',
                  defaultMessage: 'User must attach at least',
                  description: 'Label for minimum file count setting',
                })}
                settingsKey={customSettingKey.MIN_FILE_COUNT}
                width="fullWidth"
                customSettingsUpdateFactory={customSettingsUpdateFactory}
              />
            )}
          </Row>
          <Column space={2}>
            <CustomSettingsInput
              customSettings={customSettings}
              id="file--ALLOWED_FILE_TYPE_RULE"
              inputType="SINGLE_SELECT"
              label={intl.formatMessage({
                id: 'FieldSettings.allowedFileTypes',
                defaultMessage: 'Allowed file types',
                description: 'Label for allowed file types setting',
              })}
              options={fileTypeRuleOptions}
              settingsKey={customSettingKey.ALLOWED_FILE_TYPE_RULE}
              width="l"
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
            {[
              customSettingsOptionValue.FILE_TYPE_RULE.SPECIFIC,
              customSettingsOptionValue.FILE_TYPE_RULE.ANY_EXCEPT,
            ].includes(
              customSettings[customSettingKey.ALLOWED_FILE_TYPE_RULE],
            ) && (
              <CustomSettingsInput
                customSettings={customSettings}
                id="file--ALLOWED_FILE_TYPES"
                inputType="MULTI_SELECT"
                options={fileTypeOptions}
                settingsKey={customSettingKey.ALLOWED_FILE_TYPES}
                width="l"
                customSettingsUpdateFactory={customSettingsUpdateFactory}
              />
            )}
          </Column>
        </Column>
      );
    }
    case WorkflowFieldType.ShortResponse:
    case WorkflowFieldType.LongResponse: {
      return (
        <DefaultValueInput
          defaultValue={defaultValue}
          id="response--VALUE"
          isRequired={false}
          valueKey={valueKey.VALUE}
          width="l"
          defaultValueUpdateFactory={defaultValueUpdateFactory}
        />
      );
    }
    case WorkflowFieldType.MultiSelect: {
      return (
        <Column>
          <Row>
            <AvailableOptions
              id="multiSelect--options"
              options={options}
              values={options.map((option) => option.value)}
              width="l"
              onChange={handleUpdateOptions}
            />
            <DefaultValueInput
              defaultValue={defaultValue}
              disabled={!options.length}
              id="multiSelect--VALUE"
              inputType={WorkflowFieldDefaultValueInputType.MultiSelect}
              isRequired={false}
              options={options}
              valueKey={valueKey.VALUE}
              width="100%"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
              error={
                isDefaultValueInvalid
                  ? intl.formatMessage({
                      id: 'FieldSettings.selectOnlyAvailableOptions',
                      defaultMessage: 'Select only available options',
                      description: 'Error message for invalid option selection',
                    })
                  : ''
              }
            />
          </Row>
          <CustomSettingsInput
            customSettings={customSettings}
            id="multiSelect--ENABLE_CREATE_OPTIONS"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.usersCanAddOptions',
              defaultMessage: 'Users can add their own options',
              description: 'Label for enable create options checkbox',
            })}
            settingsKey={customSettingKey.ENABLE_CREATE_OPTIONS}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.MonetaryValue: {
      const currencyRuleOptions = [
        {
          label: intl.formatMessage({
            id: 'FieldSettings.anyCurrency',
            defaultMessage: 'Any currency',
            description: 'Currency rule option for any currency',
          }),
          value: customSettingsOptionValue.CURRENCY_RULE.ANY,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.anyCurrencyExcept',
            defaultMessage: 'Any currency, except',
            description: 'Currency rule option for any currency except',
          }),
          value: customSettingsOptionValue.CURRENCY_RULE.ANY_EXCEPT,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.specificCurrencyOnly',
            defaultMessage: 'Specific currency only',
            description: 'Currency rule option for specific currency',
          }),
          value: customSettingsOptionValue.CURRENCY_RULE.SPECIFIC,
        },
      ];

      return (
        <Column>
          <Row>
            <CustomSettingsInput
              customSettings={customSettings}
              id="monetary--CURRENCY_RULE"
              inputType="SINGLE_SELECT"
              label={intl.formatMessage({
                id: 'FieldSettings.allowUsersToChoose',
                defaultMessage: 'Allow users to choose',
                description: 'Label for currency rule selection',
              })}
              options={currencyRuleOptions}
              settingsKey={customSettingKey.CURRENCY_RULE}
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
            <DefaultValueInput
              defaultValue={defaultValue}
              id="monetary--CURRENCY"
              inputType={
                WorkflowFieldDefaultValueInputType.CurrencySingleSelect
              }
              label={intl.formatMessage({
                id: 'FieldSettings.defaultCurrency',
                defaultMessage: 'Default Currency',
                description: 'Label for default currency field',
              })}
              placeholder={intl.formatMessage({
                id: 'FieldSettings.selectPlaceholder',
                defaultMessage: 'Select',
                description: 'Placeholder for select inputs',
              })}
              valueKey={valueKey.CURRENCY}
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
            <DefaultValueInput
              defaultValue={defaultValue}
              id="monetary--AMOUNT"
              inputType={WorkflowFieldDefaultValueInputType.NumberInput}
              isRequired={false}
              valueKey={valueKey.AMOUNT}
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
          </Row>
          {[
            customSettingsOptionValue.CURRENCY_RULE.ANY_EXCEPT,
            customSettingsOptionValue.CURRENCY_RULE.SPECIFIC,
          ].includes(customSettings[customSettingKey.CURRENCY_RULE]) && (
            <CustomSettingsInput
              customSettings={customSettings}
              id="monetary--CURRENCY_LIST"
              inputType="CURRENCY_MULTI_SELECT"
              settingsKey={customSettingKey.CURRENCY_LIST}
              width="input.l.width"
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
          )}
          <CustomSettingsInput
            customSettings={customSettings}
            id="monetary--INCLUDE_SYMBOL_IN_DOCUMENT"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.includeCurrencySymbol',
              defaultMessage:
                'Include currency symbol when inserting into document',
              description: 'Label for include currency symbol checkbox',
            })}
            settingsKey={customSettingKey.INCLUDE_SYMBOL_IN_DOCUMENT}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.Number: {
      return (
        <Column>
          <DefaultValueInput
            defaultValue={defaultValue}
            id="number--VALUE"
            inputType={WorkflowFieldDefaultValueInputType.NumberInput}
            isRequired={false}
            valueKey={valueKey.VALUE}
            width="l"
            defaultValueUpdateFactory={defaultValueUpdateFactory}
            customSettings={customSettings}
          />
          <CustomSettingsInput
            customSettings={customSettings}
            id="number--allowDecimals"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.allowDecimals',
              defaultMessage: 'Allow decimals',
              description: 'Label for allow decimals checkbox',
            })}
            settingsKey={customSettingKey.ALLOW_DECIMALS}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.Percentage: {
      const operatorOptions = [
        {
          label: intl.formatMessage({
            id: 'FieldSettings.greaterThan',
            defaultMessage: 'Greater than',
            description: 'Operator option for greater than',
          }),
          value: customSettingsOptionValue.OPERATOR.GREATER,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.greaterThanOrEqual',
            defaultMessage: 'Greater than or equal to',
            description: 'Operator option for greater than or equal',
          }),
          value: customSettingsOptionValue.OPERATOR.GREATER_OR_EQUAL,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.lessThan',
            defaultMessage: 'Less than',
            description: 'Operator option for less than',
          }),
          value: customSettingsOptionValue.OPERATOR.LESS,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.lessThanOrEqual',
            defaultMessage: 'Less than or equal to',
            description: 'Operator option for less than or equal',
          }),
          value: customSettingsOptionValue.OPERATOR.LESS_OR_EQUAL,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.between',
            defaultMessage: 'Between',
            description: 'Operator option for between',
          }),
          value: customSettingsOptionValue.OPERATOR.BETWEEN,
        },
      ];
      let fields;
      if (
        [
          customSettingsOptionValue.OPERATOR.GREATER,
          customSettingsOptionValue.OPERATOR.GREATER_OR_EQUAL,
        ].includes(customSettings.operator)
      ) {
        fields = (
          <CustomSettingsInput
            customSettings={customSettings}
            id="percentage--MIN"
            inputType="NUMBER_INPUT"
            placeholder={intl.formatMessage({
              id: 'FieldSettings.valuePlaceholder',
              defaultMessage: 'Value',
              description: 'Placeholder for value input',
            })}
            settingsKey={customSettingKey.MIN}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        );
      } else if (
        [
          customSettingsOptionValue.OPERATOR.LESS,
          customSettingsOptionValue.OPERATOR.LESS_OR_EQUAL,
        ].includes(customSettings.operator)
      ) {
        fields = (
          <CustomSettingsInput
            customSettings={customSettings}
            id="percentage--MAX"
            inputType="NUMBER_INPUT"
            placeholder={intl.formatMessage({
              id: 'FieldSettings.valuePlaceholder',
              defaultMessage: 'Value',
              description: 'Placeholder for value input',
            })}
            settingsKey={customSettingKey.MAX}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        );
      } else if (
        customSettings.operator === customSettingsOptionValue.OPERATOR.BETWEEN
      ) {
        fields = (
          <>
            <CustomSettingsInput
              customSettings={customSettings}
              id="percentage--MIN"
              inputType="NUMBER_INPUT"
              placeholder={intl.formatMessage({
                id: 'FieldSettings.minValuePlaceholder',
                defaultMessage: 'Min value',
                description: 'Placeholder for minimum value input',
              })}
              settingsKey={customSettingKey.MIN}
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
            <CustomSettingsInput
              customSettings={customSettings}
              id="percentage--MAX"
              inputType="NUMBER_INPUT"
              placeholder={intl.formatMessage({
                id: 'FieldSettings.maxValuePlaceholder',
                defaultMessage: 'Max value',
                description: 'Placeholder for maximum value input',
              })}
              settingsKey={customSettingKey.MAX}
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
          </>
        );
      }

      return (
        <Column>
          <FlexLayout alignItems="flex-end" space={2}>
            <CustomSettingsInput
              customSettings={customSettings}
              id="percentage--operator"
              inputType="SINGLE_SELECT"
              label={intl.formatMessage({
                id: 'FieldSettings.amountShouldBe',
                defaultMessage: 'Amount should be',
                description: 'Label for operator selection',
              })}
              options={operatorOptions}
              settingsKey={customSettingKey.OPERATOR}
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
            {fields}
            <DefaultValueInput
              defaultValue={defaultValue}
              id="percentage--DEFAULT"
              inputType={WorkflowFieldDefaultValueInputType.NumberInput}
              isRequired={false}
              valueKey={valueKey.VALUE}
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
          </FlexLayout>
          <CustomSettingsInput
            customSettings={customSettings}
            id="percentage--INCLUDE_SYMBOL_IN_DOCUMENT"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.includePercentageSymbol',
              defaultMessage:
                'Include percentage symbol when inserting into document',
              description: 'Label for include percentage symbol checkbox',
            })}
            settingsKey={customSettingKey.INCLUDE_SYMBOL_IN_DOCUMENT}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.Radio: {
      return (
        <Column>
          <Row>
            <AvailableOptions
              id="radio--options"
              options={options}
              values={options.map((option) => option.value)}
              width="l"
              onChange={handleUpdateOptions}
            />
            <DefaultValueInput
              defaultValue={defaultValue}
              disabled={!options.length}
              id="radio--value"
              inputType={WorkflowFieldDefaultValueInputType.SingleSelect}
              isRequired={false}
              options={options}
              valueKey={valueKey.VALUE}
              width="100%"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
          </Row>
          <CustomSettingsInput
            customSettings={customSettings}
            description={intl.formatMessage({
              id: 'FieldSettings.userWillChooseFromOptions',
              defaultMessage:
                'User will have to choose from the options listed above',
              description: 'Description for enable other option setting',
            })}
            id="radio--ENABLE_OTHER_OPTION"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.enableOtherOption',
              defaultMessage: 'Enable "Other" option',
              description: 'Label for enable other option checkbox',
            })}
            settingsKey={customSettingKey.ENABLE_OTHER_OPTION}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
          <CustomSettingsInput
            customSettings={customSettings}
            description={intl.formatMessage({
              id: 'FieldSettings.removeOtherValueDescription',
              defaultMessage:
                'For reports and searching purposes, list the entry only as "Other"',
              description: 'Description for remove other value setting',
            })}
            disabled={!customSettings[customSettingKey.ENABLE_OTHER_OPTION]}
            id="radio--EXCLUDE_OTHER_OPTION_IN_DATA_MAPPING"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.removeOtherValue',
              defaultMessage:
                'Remove entered "Other" value in the data mapping',
              description: 'Label for remove other value checkbox',
            })}
            settingsKey={customSettingKey.EXCLUDE_OTHER_OPTION_IN_DATA_MAPPING}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.SingleSelect: {
      return (
        <Column>
          <Row>
            <AvailableOptions
              id="singleSelect--options"
              options={options}
              values={options.map((option) => option.value)}
              width="l"
              onChange={handleUpdateOptions}
            />
            <DefaultValueInput
              disabled={!options.length}
              id="singleSelect--VALUE"
              inputType={WorkflowFieldDefaultValueInputType.SingleSelect}
              options={options}
              defaultValue={defaultValue}
              valueKey={valueKey.VALUE}
              width="100%"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
              error={
                isDefaultValueInvalid
                  ? intl.formatMessage({
                      id: 'FieldSettings.selectOnlyAvailableOptions',
                      defaultMessage: 'Select only available options',
                      description: 'Error message for invalid option selection',
                    })
                  : ''
              }
            />
          </Row>
          <CustomSettingsInput
            customSettings={customSettings}
            id="singleSelect--ENABLE_CREATE_OPTIONS"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.usersCanAddOptions',
              defaultMessage: 'Users can add their own options',
              description: 'Label for enable create options checkbox',
            })}
            settingsKey={customSettingKey.ENABLE_CREATE_OPTIONS}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.TimePeriod: {
      return (
        <Column>
          <AvailableOptions
            hasPresets
            id="timePeriod--options"
            options={getTimePeriodFieldOptions()}
            values={options.map((option) => option.value)}
            width="l"
            onChange={handleUpdateOptions}
          />
          <Row>
            <DefaultValueInput
              defaultValue={defaultValue}
              disabled={!options.length}
              id="timePeriod--PERIOD"
              inputType={WorkflowFieldDefaultValueInputType.SingleSelect}
              isRequired={false}
              label={intl.formatMessage({
                id: 'FieldSettings.defaultTimePeriod',
                defaultMessage: 'Default',
                description: 'Label for default time period',
              })}
              options={options}
              placeholder={intl.formatMessage({
                id: 'FieldSettings.timeUnitPlaceholder',
                defaultMessage: 'Time unit',
                description: 'Placeholder for time unit input',
              })}
              valueKey={valueKey.PERIOD}
              width="input.m.width"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
            <DefaultValueInput
              defaultValue={defaultValue}
              disabled={!options.length}
              id="timePeriod--AMOUNT"
              inputType={WorkflowFieldDefaultValueInputType.NumberInput}
              placeholder={intl.formatMessage({
                id: 'FieldSettings.valuePlaceholder',
                defaultMessage: 'Value',
                description: 'Placeholder for value input',
              })}
              valueKey={valueKey.AMOUNT}
              width="m"
              defaultValueUpdateFactory={defaultValueUpdateFactory}
            />
          </Row>
        </Column>
      );
    }
    case WorkflowFieldType.User: {
      const ruleOptions = [
        {
          label: intl.formatMessage({
            id: 'FieldSettings.allUsers',
            defaultMessage: 'All users',
            description: 'User rule option for all users',
          }),
          value: customSettingsOptionValue.RULE.ALL,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.allUsersExcept',
            defaultMessage: 'All users, except',
            description: 'User rule option for all users except',
          }),
          value: customSettingsOptionValue.RULE.ALL_EXCEPT,
        },
        {
          label: intl.formatMessage({
            id: 'FieldSettings.specificUsers',
            defaultMessage: 'Specific users',
            description: 'User rule option for specific users',
          }),
          value: customSettingsOptionValue.RULE.SPECIFIC,
        },
      ];
      return (
        <Column>
          <CustomSettingsInput
            customSettings={customSettings}
            id="user--RULE"
            inputType="SINGLE_SELECT"
            label={intl.formatMessage({
              id: 'FieldSettings.availableUsers',
              defaultMessage: 'Available Users',
              description: 'Label for available users setting',
            })}
            options={ruleOptions}
            settingsKey={customSettingKey.RULE}
            width="l"
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
          {[
            customSettingsOptionValue.RULE.ALL_EXCEPT,
            customSettingsOptionValue.RULE.SPECIFIC,
          ].includes(customSettings[customSettingKey.RULE]) && (
            <CustomSettingsInput
              customSettings={customSettings}
              id="user--USERS"
              inputType="USER_MULTI_SELECT"
              settingsKey={customSettingKey.USERS}
              width="l"
              customSettingsUpdateFactory={customSettingsUpdateFactory}
            />
          )}
          <CustomSettingsInput
            customSettings={customSettings}
            id="user--ALLOW_MULTIPLE"
            inputType="CHECKBOX"
            label={intl.formatMessage({
              id: 'FieldSettings.allowMultiple',
              defaultMessage: 'Allow multiple',
              description: 'Label for allow multiple users checkbox',
            })}
            settingsKey={customSettingKey.ALLOW_MULTIPLE}
            customSettingsUpdateFactory={customSettingsUpdateFactory}
          />
        </Column>
      );
    }
    case WorkflowFieldType.Boolean:
    default: {
      return null;
    }
  }
}

export default FieldSettings;
